import React from 'react';
import { useTranslation } from 'react-i18next';
import { Campaign } from '../../../shared/types/Campaign';
import { TagIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  const {
    t
  } = useTranslation('shops');
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status badge styling
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      expired: 'bg-red-50 text-red-700 border border-red-100'
    };
    const style = styles[status] || styles.inactive;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-11439" data-mp="3afzih0jl3813">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <table className="w-full text-left border-collapse" data-id="element-11440" data-mp="3kahmy7wtr7ly">
      <thead data-id="element-11441" data-mp="3p3kmqxs73dal">
        <tr className="border-b border-gray-100" data-id="element-11442" data-mp="3p42na6lw4dob">
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11443" data-mp="3u2dxwjk0es7l">
            {t('components.CampaignList.campaign')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11444" data-mp="1wrx96t49b9f">
            {t('components.CampaignList.code_strategy')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11445" data-mp="bvpucy4lyd41">
            {t('components.CampaignList.discount')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11446" data-mp="2do4b3tvf1biv">
            {t('components.CampaignList.budget_usage')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11447" data-mp="25npx5cgd28re">
            {t('components.CampaignList.expiry_date')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-11448" data-mp="2flvudgr82qpx">
            {t('components.CampaignList.status')}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100" data-id="element-11449" data-mp="2olin7e5vyo7d">
        {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} onClick={() => onCampaignClick(campaign.id)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-11450" data-mp="2kk73tqdluz7m">
              <td className="px-6 py-4" data-id="element-11451" data-mp="2tdskjf9byqjo">
                <div className="text-sm font-medium text-gray-900 group-hover:text-[#9F140F] transition-colors" data-id="element-11452" data-mp="38a1ga6u0ilrc">
                  {campaign.title}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-11453" data-mp="3393s9dmbmxhx">
                  {campaign.category}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-11454" data-mp="3qz3hmlzw4cp8">
                <div className="text-sm text-gray-900" data-id="element-11455" data-mp="3qxsgyv1qrl2c">
                  {campaign.codeStrategy === 'Global' ? <span className="font-medium" data-id="element-11456" data-mp="3d16m7sx6acc5">{campaign.code}</span> : <span data-id="element-11457" data-mp="3i0003mgdg5cu">{t('components.CampaignList.generated_codes')}</span>}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-11458" data-mp="37zpmw1uc6xxo">
                  {campaign.codeStrategy}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-11459" data-mp="2u9m5bo6ejsek">
                <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-11460" data-mp="2u6hvaa69a2fz">
                  {campaign.discountPercentage > 0 ? <>
                      <PercentIcon size={14} className="mr-1 text-gray-400" data-id="element-11461" data-mp="2kaokowc3kj0u" />
                      {campaign.discountPercentage}%
                    </> : <>
                      <DollarSignIcon size={14} className="mr-1 text-gray-400" data-id="element-11462" data-mp="3sjlytbv4b29c" />
                      {campaign.fixedDiscountAmount} {campaign.currency}
                    </>}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-11463" data-mp="3dug686qscaaf">
                  {t('components.CampaignList.carried_by')}{' '}
                  {campaign.discountCarriedBy}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-11464" data-mp="34t9knj9a49ab">
                <div className="flex items-center gap-2" data-id="element-11465" data-mp="2zv81yzfrmbzd">
                  <div className="w-24 bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-11466" data-mp="2zvq76lkeo0jf">
                    <div className="bg-[#E2231A] h-1.5 rounded-full transition-all" style={{
                width: `${calculateBudgetUsage(campaign)}%`
              }} data-id="element-11467" data-mp="2uxy7349lx845"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-11468" data-mp="10nhz2at5pgrh">
                    {calculateBudgetUsage(campaign)}%
                  </span>
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-11469" data-mp="1aem7erobu3dc">
                  {campaign.budgetUsed.toLocaleString()} /{' '}
                  {campaign.budget.toLocaleString()} {campaign.currency}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-11470" data-mp="hr89orct13s6">
                <div className="text-sm text-gray-900" data-id="element-11471" data-mp="morzmoiv74zy">
                  {campaign.expiryDate}
                </div>
              </td>
              <td className="px-6 py-4 text-right" data-id="element-11472" data-mp="rmthj64k0y9u">
                {getStatusBadge(campaign.status)}
              </td>
            </tr>) : <tr data-id="element-11473" data-mp="2330b9o23jtwt">
            <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-11474" data-mp="233jgq5ak8pty">
              {t('components.CampaignList.no_campaigns_found_matching_your_criteria')}
            </td>
          </tr>}
      </tbody>
    </table>;
}